import { useState, useEffect, useRef, useCallback } from 'react'
import type { Shot, JobStatus } from '../types'
import { FoundryPicker } from '../../foundry/components/FoundryPicker'

interface Props {
  shot: Shot
  job: JobStatus
  projectId: string
  onGenerate: (promptOverride: string, referenceAssetIds?: string[]) => void
  lockedBySequence?: boolean
}

function storageKey(projectId: string, shot: Shot) {
  return `cast-prompt::${projectId}/${shot.videoId}/${shot.shotId}`
}

function assetStorageKey(projectId: string, shot: Shot) {
  return `cast-assets::${projectId}/${shot.videoId}/${shot.shotId}`
}

export function ShotCard({ shot, job, projectId, onGenerate, lockedBySequence }: Props) {
  const [expanded, setExpanded] = useState(false)
  const [prompt, setPrompt] = useState(shot.prompt)
  const [showAssets, setShowAssets] = useState(false)
  const [charAssetIds, setCharAssetIds] = useState<string[]>([])
  const [sceneAssetIds, setSceneAssetIds] = useState<string[]>([])
  const videoRef = useRef<HTMLVideoElement>(null)
  const promptKey = storageKey(projectId, shot)
  const assetKey = assetStorageKey(projectId, shot)

  useEffect(() => {
    try {
      const raw = localStorage.getItem(promptKey)
      if (raw) {
        try {
          const stored = JSON.parse(raw)
          if (stored.base === shot.prompt && stored.edit) {
            setPrompt(stored.edit)
          } else {
            localStorage.removeItem(promptKey)
          }
        } catch {
          localStorage.removeItem(promptKey)
        }
      }
    } catch {}
  }, [promptKey, shot.prompt])

  useEffect(() => {
    try {
      const raw = localStorage.getItem(assetKey)
      if (raw) {
        const stored = JSON.parse(raw) as { chars?: string[]; scenes?: string[] }
        if (stored.chars?.length) setCharAssetIds(stored.chars)
        if (stored.scenes?.length) setSceneAssetIds(stored.scenes)
      }
    } catch {}
  }, [assetKey])

  const persistAssets = useCallback((chars: string[], scenes: string[]) => {
    try {
      if (chars.length || scenes.length) {
        localStorage.setItem(assetKey, JSON.stringify({ chars, scenes }))
      } else {
        localStorage.removeItem(assetKey)
      }
    } catch {}
  }, [assetKey])

  const handleCharChange = useCallback((ids: string[]) => {
    setCharAssetIds(ids)
    persistAssets(ids, sceneAssetIds)
  }, [sceneAssetIds, persistAssets])

  const handleSceneChange = useCallback((ids: string[]) => {
    setSceneAssetIds(ids)
    persistAssets(charAssetIds, ids)
  }, [charAssetIds, persistAssets])

  const isPromptModified = prompt !== shot.prompt
  const hasAssets = charAssetIds.length > 0 || sceneAssetIds.length > 0

  const handlePromptChange = useCallback((value: string) => {
    setPrompt(value)
    try {
      if (value === shot.prompt) {
        localStorage.removeItem(promptKey)
      } else {
        localStorage.setItem(promptKey, JSON.stringify({ base: shot.prompt, edit: value }))
      }
    } catch {}
  }, [shot.prompt, promptKey])

  const resetPrompt = useCallback(() => {
    setPrompt(shot.prompt)
    try { localStorage.removeItem(promptKey) } catch {}
  }, [shot.prompt, promptKey])

  const handleGenerate = () => {
    const allAssetIds = [...charAssetIds, ...sceneAssetIds]
    onGenerate(prompt, allAssetIds.length ? allAssetIds : undefined)
  }

  const borderColor =
    job.state === 'generating' ? 'border-forge-gold' :
    job.state === 'done' ? 'border-forge-green' :
    job.state === 'failed' ? 'border-forge-heat-critical' :
    'border-forge-border'

  const glowClass =
    job.state === 'generating' ? 'shadow-[0_0_20px_rgba(255,184,0,0.08)]' : ''

  return (
    <div className={`bg-forge-basalt border ${borderColor} rounded-xl overflow-hidden transition-all duration-200 hover:border-forge-slate ${glowClass}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 p-4 pb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-forge-primary truncate">{shot.label}</h3>
          <div className="flex gap-2 mt-1.5 flex-wrap">
            <span className="text-[10px] px-2 py-0.5 rounded bg-forge-obsidian text-forge-ash">{shot.aspectRatio}</span>
            <span className="text-[10px] px-2 py-0.5 rounded bg-forge-obsidian text-forge-ash">{shot.durationHint}s</span>
            {shot.group && (
              <span className="text-[10px] px-2 py-0.5 rounded bg-forge-ember/15 text-forge-ember font-medium">
                {shot.group} #{shot.sequence}
              </span>
            )}
            {isPromptModified && (
              <span className="text-[10px] px-2 py-0.5 rounded bg-forge-gold/15 text-forge-gold font-medium">
                edited
              </span>
            )}
            {hasAssets && (
              <span className="text-[10px] px-2 py-0.5 rounded bg-forge-ember/15 text-forge-ember font-medium">
                {charAssetIds.length + sceneAssetIds.length} ref{charAssetIds.length + sceneAssetIds.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
        <div className="w-8 h-8 rounded-lg bg-forge-ember/10 flex items-center justify-center flex-shrink-0">
          <span className="text-forge-ember font-display font-bold text-xs">{shot.shotNumber}</span>
        </div>
      </div>

      {/* Prompt */}
      <div className="px-4 pb-3">
        {expanded ? (
          <div className="space-y-2">
            <textarea
              value={prompt}
              onChange={(e) => handlePromptChange(e.target.value)}
              rows={8}
              className="w-full bg-forge-obsidian border border-forge-border rounded-lg p-3 font-mono text-[11px] leading-relaxed text-forge-ash resize-y focus:outline-none focus:border-forge-ember/50 transition-colors"
            />
            <div className="flex items-center gap-3">
              {isPromptModified && (
                <button
                  onClick={resetPrompt}
                  className="text-[10px] text-forge-gold hover:text-forge-gold/80 transition-colors"
                >
                  Reset to default
                </button>
              )}
              <div className="flex-1" />
              <button
                onClick={() => setExpanded(false)}
                className="text-[10px] text-forge-cool-smoke hover:text-forge-ash transition-colors"
              >
                Collapse
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setExpanded(true)}
            className="w-full text-left"
          >
            <div className="font-mono text-[11px] leading-relaxed text-forge-ash max-h-[72px] overflow-hidden">
              {prompt}
            </div>
            {prompt.length > 200 && (
              <div className="h-6 bg-gradient-to-t from-forge-basalt to-transparent -mt-6 relative" />
            )}
            <span className="text-[10px] text-forge-cool-smoke mt-1 inline-block">
              Click to edit prompt
            </span>
          </button>
        )}
      </div>

      {/* Reference Assets — collapsible */}
      {!lockedBySequence && (
        <div className="px-4 pb-3">
          <button
            onClick={() => setShowAssets(!showAssets)}
            className="text-[10px] font-semibold text-forge-ember hover:text-forge-ember/80 transition-colors"
          >
            {showAssets ? '− Hide Reference Assets' : '+ Reference Assets'}
            {hasAssets && !showAssets && ` (${charAssetIds.length + sceneAssetIds.length} selected)`}
          </button>
          {showAssets && (
            <div className="mt-2 space-y-2">
              <div>
                <span className="text-[9px] uppercase tracking-wider text-forge-ash font-semibold block mb-1">Character</span>
                <FoundryPicker
                  type="character"
                  selectedIds={charAssetIds}
                  maxSelections={2}
                  onSelectionChange={handleCharChange}
                />
              </div>
              <div>
                <span className="text-[9px] uppercase tracking-wider text-forge-ash font-semibold block mb-1">Scene</span>
                <FoundryPicker
                  type="scene"
                  selectedIds={sceneAssetIds}
                  maxSelections={2}
                  onSelectionChange={handleSceneChange}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Video Preview */}
      {job.state === 'done' && job.file && (
        <div className="px-4 pb-3">
          <video
            ref={videoRef}
            src={job.file}
            controls
            preload="metadata"
            className="w-full rounded-lg bg-black"
          />
        </div>
      )}

      {/* Logs */}
      {job.logs && job.logs.length > 0 && (
        <div className="px-4 pb-3">
          <div className="max-h-[60px] overflow-y-auto font-mono text-[10px] text-forge-cool-smoke leading-relaxed">
            {job.logs.map((l, i) => (
              <div key={i}>{l.msg}</div>
            ))}
          </div>
        </div>
      )}

      {/* Error */}
      {job.error && (
        <div className="px-4 pb-3">
          <p className="text-[11px] text-forge-heat-critical">{job.error}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 px-4 py-3 border-t border-forge-border/50">
        <StatusDot state={job.state} />
        <span className="text-[11px] text-forge-ash">{statusLabel(job)}</span>
        <div className="flex-1" />
        {job.state === 'done' && job.file && (
          <a
            href={job.file}
            download
            className="text-[11px] font-semibold text-forge-ash hover:text-forge-primary border border-forge-border rounded-md px-3 py-1 transition-colors"
          >
            Download
          </a>
        )}
        {lockedBySequence ? (
          <span className="text-[10px] text-forge-cool-smoke italic">Part of sequence</span>
        ) : (
          <button
            onClick={handleGenerate}
            disabled={job.state === 'generating'}
            className={`text-[11px] font-semibold rounded-md px-4 py-1.5 transition-all ${
              job.state === 'generating'
                ? 'bg-forge-slate text-forge-cool-smoke cursor-not-allowed'
                : 'bg-forge-ember text-white hover:bg-forge-ember/80'
            }`}
          >
            {job.state === 'done' ? 'Regenerate' : job.state === 'generating' ? 'Generating...' : 'Generate'}
          </button>
        )}
      </div>
    </div>
  )
}

function StatusDot({ state }: { state: string }) {
  const color =
    state === 'generating' ? 'bg-forge-gold animate-pulse' :
    state === 'done' ? 'bg-forge-green' :
    state === 'failed' ? 'bg-forge-heat-critical' :
    'bg-forge-cool-smoke'

  return <div className={`w-2 h-2 rounded-full ${color}`} />
}

function statusLabel(job: JobStatus): string {
  if (job.state === 'idle') return 'Ready'
  if (job.state === 'generating') {
    const sec = Math.round((job.elapsed || 0) / 1000)
    return sec > 0 ? `Generating (${sec}s)` : 'Generating...'
  }
  if (job.state === 'done') return 'Complete'
  if (job.state === 'failed') return 'Failed'
  return job.state
}
